import { useState, useCallback } from 'react';
import { UserCredentials } from '../services/wpAuth';
import { getStoredCredentials } from '../utils/auth';

/**
 * Custom hook for executing authenticated actions with loading and error states
 * @returns Object with execute function and state
 */
export const useAuthenticatedAction = <TResult = any, TArgs extends any[] = any[]>() => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const execute = useCallback(async (
        action: (credentials: UserCredentials, ...args: TArgs) => Promise<TResult>,
        ...args: TArgs
    ): Promise<TResult | null> => {
        const credentials = getStoredCredentials();
        if (!credentials) {
            setError("Vous devez être connecté pour effectuer cette action");
            return null;
        }

        setLoading(true);
        setError(null);

        try {
            const result = await action(credentials, ...args);
            return result;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Une erreur s'est produite";
            setError(errorMessage);
            console.error("Authenticated action failed:", err);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        execute,
        loading,
        error,
        clearError
    };
};
